import Modal from "@/common/components/Modal";
import SelectController from "@/common/components/SelectController";
import TextField from "@/common/components/TextField";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { dashboardKind } from "../../constants/dashboards/dashboardKind.constant";
import { useUpdateDashboardComponents } from "../../hooks/dashboards/useUpdateDashboardComponent";
import { updateDashboardComponentSchema, UpdateDashboardComponentValidationSchema } from "../../schemas/dashboards/dashboardComponent";
import { DashboardComponentData } from "../../types/dashboards/dashboardComponent.type";

interface UpdateDashboardComponentProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  dashboardId: string;
  dashboardComponent: DashboardComponentData | undefined;
}

const UpdateDashboardComponent = ({
  isOpen,
  setIsOpen,
  dashboardId,
  dashboardComponent,
}: UpdateDashboardComponentProps) => {
  const queryClient = useQueryClient();

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const {
    control,
    setError,
    handleSubmit,
    reset,
    formState: {
      errors,
    },
  } = useForm<UpdateDashboardComponentValidationSchema>({
    resolver: zodResolver(updateDashboardComponentSchema),
  });


  const {
    mutate: updateDashboardComponent,
    isPending: updateDashboardComponentIsPending,
  } = useUpdateDashboardComponents({
    dashboardId,
    dashboardComponentId: dashboardComponent?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['dashboard', dashboardId],
        });

        queryClient.invalidateQueries({
          queryKey: ['dashboardComponent', dashboardComponent?.id],
        });

        setIsOpen(false);
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<UpdateDashboardComponentValidationSchema> = (data) => updateDashboardComponent(data);

  const kindOptions = useMemo(() => {
    if (!dashboardComponent) {
      return [];
    }

    return dashboardComponent.definition.available_kinds?.map((kind) => ({
      value: kind,
      label: dashboardKind[kind] || kind,
    }));
  }, [isOpen]);

  useEffect(() => {
    reset({
      kind: dashboardComponent?.data?.kind ? {
        label: dashboardKind[dashboardComponent?.data?.kind],
        value: dashboardComponent?.data?.kind,
      } : undefined,
    });
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="rounded-lg overflow-hidden"
      title="Editar gráfico"
      bodyClassName="text-white-800"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-200"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={updateDashboardComponentIsPending}
      body={(
        <form className="flex flex-col gap-4">
          <TextField
            label="Gráfico"
            disabled
            value={dashboardComponent?.definition?.title}
          />

          <SelectController
            control={control}
            name="kind"
            label="Modelo"
            markAsRequired
            options={kindOptions}
            errorMessage={errors?.kind?.message}
          />
        </form>
      )}
    />
  );
}

export default UpdateDashboardComponent;
